import { useMemo, useState } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentType,
  ComplianceProgramStep,
} from "types/graphql-schema";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import { ProgramForm, ProgramFormStep } from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import QuestionSetSelector from "./question-set-selector";

const EngagementSurveyStep = ({
  proposedComplianceProgram,
  canUpdate,
  availableAssessmentGroups,
  onAddOrUpdateStep,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
}) => {
  const [isAssessmentGroupSidebarOpen, setIsAssessmentGroupSidebarOpen] =
    useState(false);
  const [assessmentGroupToDuplicate, setAssessmentGroupToDuplicate] =
    useState<AssessmentGroupListFragmentFragment | null>(null);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(
        ({ assessmentType }) =>
          assessmentType === AssessmentType.EngagementSurvey,
      )
      .map(({ id, title }) => ({
        value: id,
        label: title,
      }));
  }, [availableAssessmentGroups]);

  const engagementSurveyStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.EngagementSurvey,
    );
  }, [proposedComplianceProgram]);

  const selectedAssessmentGroup = useMemo(() => {
    return engagementSurveyStep &&
      engagementSurveyStep.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === engagementSurveyStep.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, engagementSurveyStep]);

  return (
    engagementSurveyStep && (
      <>
        {isAssessmentGroupSidebarOpen && (
          <AssessmentGroupSidebar
            assessmentGroupId={selectedAssessmentGroup?.id}
            assessmentType={AssessmentType.EngagementSurvey}
            assessmentGroupToDuplicateId={assessmentGroupToDuplicate?.id}
            onClose={() => {
              setAssessmentGroupToDuplicate(null);
              setIsAssessmentGroupSidebarOpen(false);
            }}
            onAssessmentGroupCreated={(assessmentGroup) => {
              onAssessmentGroupCreated(assessmentGroup);
              onAddOrUpdateStep({
                ...engagementSurveyStep,
                questionSetIds: [assessmentGroup.id],
              });
            }}
            onAssessmentGroupArchived={(assessmentGroupId) =>
              onAssessmentGroupArchived(assessmentGroupId)
            }
          />
        )}
        <ComplianceProgramFormStep title="Survey">
          <div className="px-6 pb-6 flex flex-col gap-3">
            <div className="flex items-center gap-3 p-4 bg-white border rounded">
              <QuestionSetSelector
                assessmentType={AssessmentType.EngagementSurvey}
                availableAssessmentGroupSelectOptions={
                  availableAssessmentGroupSelectOptions
                }
                canUpdate={canUpdate}
                onUpdateAssessmentTemplate={(assessmentGroupId) => {
                  onAddOrUpdateStep({
                    ...engagementSurveyStep,
                    questionSetIds: assessmentGroupId
                      ? [assessmentGroupId]
                      : [],
                  });
                }}
                onDuplicateAssessmentGroup={setAssessmentGroupToDuplicate}
                onOpenAssessmentGroupSidebar={() =>
                  setIsAssessmentGroupSidebarOpen(true)
                }
                selectedAssessmentGroup={selectedAssessmentGroup}
              />
            </div>
          </div>
        </ComplianceProgramFormStep>
      </>
    )
  );
};
export default EngagementSurveyStep;
